import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  Typography,
  Stack,
  FormControlLabel,
  Switch,
  Checkbox,
} from '@mui/material';
import { extensionsToMimeTypes } from '../../utils/mimeTypes';

// File type categories with their extensions
const FILE_TYPE_CATEGORIES = {
  document: {
    extensions: ['doc', 'docx', 'txt', 'rtf', 'odt'],
  },
  presentation: {
    extensions: ['ppt', 'pptx', 'odp', 'key'],
  },
  spreadsheet: {
    extensions: ['xls', 'xlsx', 'csv', 'ods', 'numbers'],
  },
  drawing: {
    extensions: ['svg', 'ai', 'eps', 'sketch', 'fig'],
  },
  pdf: {
    extensions: ['pdf'],
  },
  image: {
    extensions: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tiff', 'tif', 'ico'],
  },
  video: {
    extensions: ['mp4', 'avi', 'mkv', 'mov', 'webm', 'flv', 'wmv'],
  },
  audio: {
    extensions: ['mp3', 'wav', 'flac', 'aac', 'ogg', 'm4a', 'wma'],
  },
};

export default function FileUploadOptions({ data, updateValidationRules }) {
  const { t } = useTranslation(['procedures']);
  const [restrictFileTypes, setRestrictFileTypes] = useState(
    (data.validation_rules?.allowed_types || []).length > 0
  );
  const [selectedCategories, setSelectedCategories] = useState(() => {
    // Initialize selected categories based on current allowed_types
    const currentTypes = data.validation_rules?.allowed_types || [];
    if (currentTypes.length === 0) return [];

    // This is a simplified approach - in reality you'd need to check which categories match
    return [];
  });

  const handleToggleRestriction = async (enabled) => {
    setRestrictFileTypes(enabled);

    if (!enabled) {
      // Clear all restrictions
      updateValidationRules({
        allowed_types: [],
      });
      setSelectedCategories([]);
    }
  };

  const handleCategoryToggle = async (categoryKey) => {
    const isSelected = selectedCategories.includes(categoryKey);
    let newSelectedCategories;

    if (isSelected) {
      // Remove category
      newSelectedCategories = selectedCategories.filter((key) => key !== categoryKey);
    } else {
      // Add category
      newSelectedCategories = [...selectedCategories, categoryKey];
    }

    setSelectedCategories(newSelectedCategories);

    // Collect all extensions from selected categories
    const allExtensions = newSelectedCategories.flatMap(
      (key) => FILE_TYPE_CATEGORIES[key].extensions
    );

    // Convert to MIME types
    const mimeTypes = await extensionsToMimeTypes(allExtensions);

    updateValidationRules({
      allowed_types: mimeTypes.sort(),
    });
  };

  return (
    <>
      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
        {t('procedures:file_upload.title')}
      </Typography>
      <Stack spacing={2}>
        <Box>
          {/* Toggle for restricting file types */}
          <FormControlLabel
            control={
              <Switch
                checked={restrictFileTypes}
                onChange={(e) => handleToggleRestriction(e.target.checked)}
              />
            }
            label={t('procedures:file_upload.restrict_types_label')}
            labelPlacement='end'
          />

          {/* File type checkboxes (only shown when restriction is enabled) */}
          {restrictFileTypes && (
            <Box sx={{ pl: 2 }}>
              {/* <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
                {t('procedures:file_upload.select_types_label')}
              </Typography> */}
              <Box display={"grid"} gridTemplateColumns={"1fr 1fr"} maxWidth={"50%"}>
                {Object.keys(FILE_TYPE_CATEGORIES).map((key) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Checkbox
                        sx={{ p: 1 }}
                        checked={selectedCategories.includes(key)}
                        onChange={() => handleCategoryToggle(key)}
                      />
                    }
                    label={t(`procedures:file_upload.categories.${key}`)}
                  />
                ))}
              </Box>

              {/* Show selected count */}
              {selectedCategories.length > 0 && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                  {t(`procedures:file_upload.types_selected${selectedCategories.length === 1 ? '' : '_plural'}`, { count: selectedCategories.length })}
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {/* Max file size */}
        <TextField
          fullWidth
          type="number"
          sx={{ maxWidth: "50%" }}
          label={t('procedures:file_upload.max_file_size')}
          value={data.validation_rules?.max_size_mb || 10}
          onChange={(e) => updateValidationRules({ max_size_mb: parseInt(e.target.value) || 10 })}
          inputProps={{ min: 1, max: 100 }}
        />
      </Stack>
    </>
  );
}
